import React, { useEffect, useRef } from 'react';
import { Button, Form, Input, Spin, Typography } from 'antd';
import { useMutation } from '@apollo/client';
// Api
import { GET_BRAND_RESTRICTED_CATEGORIES } from 'api/brandRestrictedCategory/queries';
import {
  ADD_BRAND_RESTRICTED_CATEGORY,
  UPDATE_BRAND_RESTRICTED_CATEGORY,
} from 'api/brandRestrictedCategory/mutations';
// Types
import {
  AddBrandRestrictedCategory,
  AddBrandRestrictedCategoryVariables,
} from 'api/brandRestrictedCategory/types/AddBrandRestrictedCategory';
import {
  UpdateBrandRestrictedCategory,
  UpdateBrandRestrictedCategoryVariables,
} from 'api/brandRestrictedCategory/types/UpdateBrandRestrictedCategory';
import { GetBrandRestrictedCategories_getBrandRestrictedCategories } from 'api/brandRestrictedCategory/types/GetBrandRestrictedCategories';
// UI
import { successNotification, errorNotification } from 'ui/Notification';

type FormValues = {
  name: string;
};

export type CreateEditBrandRestrictedCategoryProps = {
  record: GetBrandRestrictedCategories_getBrandRestrictedCategories | null;
  onFinish: () => void;
};

const { Text } = Typography;

const CreateEditBrandRestrictedCategory = ({
  record,
  onFinish,
}: CreateEditBrandRestrictedCategoryProps): JSX.Element => {
  const isEdit = !!record?.id;
  const inputRef = useRef<Input>(null);

  const initialFormValues: FormValues = {
    name: record?.name || '',
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [addBrandRestrictedCategory, { loading: addLoading }] = useMutation<
    AddBrandRestrictedCategory,
    AddBrandRestrictedCategoryVariables
  >(ADD_BRAND_RESTRICTED_CATEGORY, {
    refetchQueries: [{ query: GET_BRAND_RESTRICTED_CATEGORIES }],
  });

  const [updateBrandRestrictedCategory, { loading: updateLoading }] =
    useMutation<
      UpdateBrandRestrictedCategory,
      UpdateBrandRestrictedCategoryVariables
    >(UPDATE_BRAND_RESTRICTED_CATEGORY, {
      refetchQueries: [{ query: GET_BRAND_RESTRICTED_CATEGORIES }],
    });

  const handleCreate = async (values: FormValues) => {
    try {
      await addBrandRestrictedCategory({
        variables: {
          input: {
            name: values.name.trim(),
          },
        },
      });

      successNotification('Restricted category created');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleUpdate = async (values: FormValues) => {
    if (!record?.id) {
      return;
    }

    try {
      await updateBrandRestrictedCategory({
        variables: {
          input: {
            id: record.id,
            name: values.name.trim(),
          },
        },
      });

      successNotification('Restricted category updated');
      onFinish();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (isEdit) {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
    }
  };

  const loading = addLoading || updateLoading;

  return (
    <Form
      layout="vertical"
      name="createEditBrandRestrictedCategory"
      initialValues={initialFormValues}
      autoComplete="off"
      onFinish={handleSubmit}
    >
      <Form.Item
        name="name"
        label={<Text>Name</Text>}
        rules={[
          {
            required: true,
            message: 'Please enter the category name',
            whitespace: true,
          },
        ]}
      >
        <Input ref={inputRef} placeholder="e.g. Alcohol, Gambling, Adult" />
      </Form.Item>

      {loading ? (
        <Spin size="large" />
      ) : (
        <Button type="primary" htmlType="submit">
          {isEdit ? 'Update' : 'Create'}
        </Button>
      )}
    </Form>
  );
};

export default CreateEditBrandRestrictedCategory;
